import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function Lightbox({ images, openIndex, onClose }) {
    const [idx, setIdx] = useState(openIndex ?? 0);

    useEffect(() => { setIdx(openIndex ?? 0); }, [openIndex]);

    const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
    const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

    useEffect(() => {
        if (openIndex === null || openIndex === undefined) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [openIndex, onClose, prev, next]);

    if (openIndex === null || openIndex === undefined) return null;
    if (!images || images.length === 0) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-forestDark/95 backdrop-blur-md flex items-center justify-center"
            onClick={onClose}
            data-testid="lightbox"
        >
            <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                data-testid="lightbox-close"
                className="absolute top-6 right-6 text-bone hover:text-terracotta z-10 p-2"
                aria-label="Close"
            >
                <X size={28} />
            </button>

            {images.length > 1 && (
                <>
                    <button
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        data-testid="lightbox-prev"
                        className="absolute left-6 top-1/2 -translate-y-1/2 text-bone hover:text-terracotta p-2"
                        aria-label="Previous"
                    >
                        <ChevronLeft size={36} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        data-testid="lightbox-next"
                        className="absolute right-6 top-1/2 -translate-y-1/2 text-bone hover:text-terracotta p-2"
                        aria-label="Next"
                    >
                        <ChevronRight size={36} />
                    </button>
                </>
            )}

            <div
                className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={images[idx]}
                    alt={`Image ${idx + 1}`}
                    className="max-w-full max-h-[80vh] object-contain"
                    data-testid="lightbox-image"
                />
                <div className="mt-4 text-bone/70 text-xs uppercase tracking-widest2">
                    {idx + 1} / {images.length}
                </div>
            </div>
        </div>
    );
}
